import { activeLocale } from "./lib/locale-format.js";
// Peças compartilhadas das telas de chamadas (monitoramento + relatórios).
// Helpers de formatação, badge semântico de status e a tabela reutilizável.
import { useState } from "react";
import { useTranslation } from "react-i18next";
import "./i18n.js";
import { ArrowDownLeft, ArrowUpRight } from "lucide-react";
import { callFailureLabel, type CallListItem, type CallStatus } from "./api.js";
import { useConversionStages } from "@/hooks/use-calls";
import { useDispositions } from "@/hooks/use-dispositions";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CallFormButton } from "./post-call-form.js";
import { CallDetailDialog } from "./call-detail-dialog.js";
import { NoDataIllustration } from "@/components/empty-illustrations";
import { Empty, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty";
import { CallRecordingActions } from "./call-recording-actions.js";
import { useAccessSnapshot } from "@/hooks/use-viewer-role";

// Duração em segundos → mm:ss (ou h:mm:ss). "—" quando ausente.
export function formatDuration(seconds: number | null): string {
  if (seconds === null || seconds < 0) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const mm = String(m).padStart(2, "0");
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

// E.164/BR simples: 55DD9XXXXXXXX → (DD) 9XXXX-XXXX. Sem heurística agressiva:
// se não bater o formato esperado, devolve o número cru.
export function formatPhone(raw: string | null): string {
  if (!raw) return "—";
  const digits = raw.replace(/\D/g, "");
  const local = digits.startsWith("55") ? digits.slice(2) : digits;
  if (local.length === 11) {
    return `(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`;
  }
  if (local.length === 10) {
    return `(${local.slice(0, 2)}) ${local.slice(2, 6)}-${local.slice(6)}`;
  }
  return raw;
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString(activeLocale());
}

// Cores semânticas por status (não só decorativas): completada=verde, em
// andamento/chamando=âmbar/azul, falha=vermelho, não atendida=neutro.
const STATUS_CLASS: Record<CallStatus, string> = {
  COMPLETED: "border-transparent bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
  IN_PROGRESS: "border-transparent bg-sky-500/15 text-sky-700 dark:text-sky-400",
  RINGING: "border-transparent bg-amber-500/15 text-amber-700 dark:text-amber-400",
  FAILED: "border-transparent bg-destructive/15 text-destructive",
  NO_ANSWER: "border-transparent bg-muted text-muted-foreground",
};

// Tabulação só faz sentido para chamada encerrada — status terminal.
const TERMINAL_STATUSES: ReadonlySet<CallStatus> = new Set(["COMPLETED", "FAILED", "NO_ANSWER"]);

export function StatusBadge({ status }: { status: CallStatus }) {
  const { t } = useTranslation();
  const pulse = status === "RINGING" || status === "IN_PROGRESS";
  return (
    <Badge className={STATUS_CLASS[status]}>
      {pulse && (
        <span className="mr-1.5 inline-block size-1.5 animate-pulse rounded-full bg-current" />
      )}
      {t(`calls.common.status.${status}`)}
    </Badge>
  );
}

// Tabulação: só o rótulo gravado. Não há modo de edição — o desfecho é derivado
// da telefonia pela auto-tabulação (back features/call-finalize), nunca escolhido
// à mão.
function DispositionLabel({
  dispositionId,
  failureReason,
  status,
}: {
  dispositionId: string | null;
  failureReason: string | null;
  status: CallStatus;
}) {
  const { t } = useTranslation();
  const { data: dispositions } = useDispositions({ enabled: dispositionId !== null });
  const label = dispositionId
    ? (dispositions?.find((d) => d.id === dispositionId)?.label ?? null)
    : null;
  const fallback = failureReason
    ? t(`calls.common.failureReasons.${failureReason}`, {
        defaultValue: callFailureLabel(failureReason) ?? failureReason,
      })
    : status === "COMPLETED"
      ? t("calls.common.completedDisposition")
      : null;

  return label || fallback ? (
    <span className="text-sm">{label ?? fallback}</span>
  ) : (
    <span className="text-muted-foreground">—</span>
  );
}

// Mesma normalização do back (normalizeStage em features/conversions/schema.ts):
// trim + caixa baixa, sem folding de acento. Qualquer outra normalização faz o
// badge divergir do que GET /v1/conversion-stages de fato conta.
function normalizeStage(stage: string): string {
  return stage.trim().toLowerCase();
}

// Etapa ATUAL do lead, classificada contra o mesmo catálogo que decide a taxa
// de conversão. Verde = conta como conversão, vermelho = conta como perda,
// neutro = etapa que ninguém configurou — de propósito, para saltar aos olhos
// na listagem em vez de se camuflar como "tanto faz".
function StageBadge({ leadStage }: { leadStage: string | null }) {
  const { data } = useConversionStages({ enabled: leadStage !== null });
  if (leadStage === null) return <span className="text-muted-foreground">—</span>;
  const match = data?.stages.find((s) => normalizeStage(s.stage) === normalizeStage(leadStage));
  const classes =
    match?.result === "CONVERSION"
      ? "border-transparent bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
      : match?.result === "LOSS"
        ? "border-transparent bg-destructive/15 text-destructive"
        : "border-transparent bg-muted text-muted-foreground";
  return <Badge className={classes}>{leadStage}</Badge>;
}

function DirectionCell({ item }: { item: CallListItem }) {
  const { t } = useTranslation();
  const Icon = item.direction === "INBOUND" ? ArrowDownLeft : ArrowUpRight;
  const number = item.direction === "INBOUND" ? item.callerDid : item.targetPhone;
  // Número discado mascarado por papel (vendedor não vê o número no histórico).
  const outHidden = item.direction === "OUTBOUND" && item.targetPhoneHidden;
  return (
    <div className="flex items-center gap-2">
      <Icon
        className={
          item.direction === "INBOUND"
            ? "size-4 shrink-0 text-emerald-600 dark:text-emerald-400"
            : "size-4 shrink-0 text-sky-600 dark:text-sky-400"
        }
        aria-label={t(`calls.common.direction.${item.direction}`)}
      />
      <span className="font-medium tabular-nums">
        {outHidden ? t("calls.common.hiddenNumber") : formatPhone(number)}
      </span>
    </div>
  );
}

// Tabela reutilizável. `showAgent` liga a coluna Responsável (relatórios da org);
// `showDuration` liga as duas colunas de tempo — falado e espera — que só fazem
// sentido depois que a chamada terminou (irrelevantes em chamadas ativas);
// `showCallForm` decide se a coluna Tabulação traz o botão do formulário
// pós-ligação ao lado do desfecho (monitoramento) ou só o desfecho (relatórios);
// `showStage` liga a coluna Etapa atual — só a Visão geral liga (é onde a taxa
// de conversão por virada de etapa vive), nenhuma outra tela deve ligar.
export function CallsTable({
  calls,
  showAgent = true,
  showDuration = true,
  showDisposition = true,
  showCallForm = true,
  showStage = false,
  emptyLabel,
}: {
  calls: CallListItem[];
  showAgent?: boolean;
  showDuration?: boolean;
  showDisposition?: boolean;
  showCallForm?: boolean;
  showStage?: boolean;
  emptyLabel?: string;
}) {
  const { t } = useTranslation();
  const { permissions } = useAccessSnapshot();
  const canDeleteRecording = permissions.has("reports.calls.removeRecording");
  const resolvedEmptyLabel = emptyLabel ?? t("calls.common.empty");
  // showDuration vale por DUAS colunas (tempo falado + espera). Áudio é uma
  // coluna fixa: é onde a pessoa baixa ou exclui só a gravação.
  const colCount =
    5 +
    (showAgent ? 1 : 0) +
    (showDuration ? 2 : 0) +
    (showDisposition ? 1 : 0) +
    (showStage ? 1 : 0);
  // Ligação aberta no diálogo de detalhe (clique na linha).
  const [selected, setSelected] = useState<CallListItem | null>(null);
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("calls.common.columns.number")}</TableHead>
            <TableHead>{t("calls.common.columns.lead")}</TableHead>
            {showAgent && <TableHead>{t("calls.common.columns.owner")}</TableHead>}
            <TableHead>{t("calls.common.columns.status")}</TableHead>
            {showDisposition && <TableHead>{t("calls.common.columns.disposition")}</TableHead>}
            {showStage && <TableHead>{t("calls.common.columns.currentStage")}</TableHead>}
            {showDuration && (
              <TableHead className="text-right">{t("calls.common.columns.talkTime")}</TableHead>
            )}
            {showDuration && (
              <TableHead className="text-right">{t("calls.common.columns.wait")}</TableHead>
            )}
            <TableHead className="text-right">{t("calls.common.columns.recording")}</TableHead>
            <TableHead className="text-right">{t("calls.common.columns.startedAt")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {calls.length === 0 && (
            <TableRow>
              <TableCell colSpan={colCount}>
                <Empty>
                  <EmptyHeader>
                    <EmptyMedia>
                      <NoDataIllustration />
                    </EmptyMedia>
                    <EmptyTitle>{resolvedEmptyLabel}</EmptyTitle>
                  </EmptyHeader>
                </Empty>
              </TableCell>
            </TableRow>
          )}
          {calls.map((c) => (
            <TableRow
              key={c.id}
              tabIndex={0}
              onClick={() => setSelected(c)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setSelected(c);
                }
              }}
              className="cursor-pointer focus-visible:bg-muted/60 focus-visible:outline-none"
            >
              <TableCell>
                <DirectionCell item={c} />
              </TableCell>
              <TableCell className="text-sm">
                {c.leadName ? (
                  // title: o nome inteiro fica acessível quando a coluna trunca.
                  <span className="block max-w-[16rem] truncate" title={c.leadName}>
                    {c.leadName}
                  </span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
              {showAgent && (
                <TableCell className="text-sm">
                  {c.agentName ?? <span className="text-muted-foreground">—</span>}
                </TableCell>
              )}
              <TableCell>
                <StatusBadge status={c.status} />
              </TableCell>
              {showDisposition && (
                <TableCell>
                  <div className="flex items-center gap-2">
                    <DispositionLabel
                      dispositionId={c.dispositionId}
                      failureReason={c.failureReason}
                      status={c.status}
                    />
                    {showCallForm && TERMINAL_STATUSES.has(c.status) && (
                      <span onClick={(e) => e.stopPropagation()}>
                        <CallFormButton callId={c.id} />
                      </span>
                    )}
                  </div>
                </TableCell>
              )}
              {showStage && (
                <TableCell>
                  <StageBadge leadStage={c.leadStage} />
                </TableCell>
              )}
              {showDuration && (
                <TableCell className="text-right tabular-nums text-sm">
                  {formatDuration(c.duration)}
                </TableCell>
              )}
              {showDuration && (
                <TableCell className="text-right tabular-nums text-sm text-muted-foreground">
                  {formatDuration(c.waitSeconds)}
                </TableCell>
              )}
              <TableCell className="text-right">
                {c.recordingName ? (
                  <CallRecordingActions callId={c.id} canDelete={canDeleteRecording} />
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell className="text-right text-sm text-muted-foreground tabular-nums">
                {formatDateTime(c.startedAt ?? c.createdAt)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <CallDetailDialog
        call={selected}
        open={selected !== null}
        onOpenChange={(v) => !v && setSelected(null)}
        canDeleteRecording={canDeleteRecording}
      />
    </div>
  );
}
